import {
  assetCreateShowsRegionField,
  resolveIsraelDefaultRegionId,
} from './assetCreateFormDefaults';
import type { GeneralInfoPicklistItem } from '../api/generalInfoApi';

/** Hidden on private-client register UI until backend no longer requires them. */
export const PRIVATE_CLIENT_HIDDEN_REGISTER_KEYS = [
  'address',
  'gender',
  'national_id',
] as const;

/** Omitted from profile-completion UI — signed-in users already have credentials. */
export const PRIVATE_CLIENT_COMPLETION_HIDDEN_KEYS = ['password', 'confirm_password'] as const;

/** Not accepted on POST /register/PrivateClient — use PATCH /profile after sign-in. */
export const PRIVATE_CLIENT_REGISTER_POST_OMIT_KEYS = ['occupation'] as const;

/** Placeholder address when the user does not fill one in (no `building` — DTO rejects it). */
export const PRIVATE_CLIENT_REGISTER_DEFAULT_ADDRESS: Record<string, unknown> = {
  street: 'N/A',
  city: 'N/A',
  zip_code: '00000',
  address2: '',
};

/** Wire placeholders merged on submit when fields are not shown in the form. */
export const PRIVATE_CLIENT_REGISTER_BACKEND_DEFAULTS: Record<string, unknown> = {
  address: PRIVATE_CLIENT_REGISTER_DEFAULT_ADDRESS,
  gender: 'other',
  national_id: '000000000',
  state_id: 1,
};

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function pickString(value: unknown): string {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return '';
}

function coerceNumericId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function isEmptyRegisterValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return !value.trim();
  }
  return false;
}

function addressLooksComplete(address: unknown): boolean {
  if (!isPlainObject(address)) {
    return false;
  }
  return Boolean(
    pickString(address.street) &&
      pickString(address.city) &&
      (pickString(address.zip_code) || pickString(address.postal_code)),
  );
}

/** PrivateClient register DTO requires `address` object but forbids `building`. */
export function normalizePrivateClientRegisterAddress(
  address: unknown,
): Record<string, unknown> {
  const source = addressLooksComplete(address) && isPlainObject(address)
    ? { ...address }
    : { ...PRIVATE_CLIENT_REGISTER_DEFAULT_ADDRESS };
  delete source.building;
  return source;
}

function stripPrivateClientRegisterOmittedKeys(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...payload };
  for (const key of PRIVATE_CLIENT_REGISTER_POST_OMIT_KEYS) {
    delete out[key];
  }
  return out;
}

export function isPrivateClientRegisterFlow(
  selectedOption: string,
  privateClientCompletionMode: boolean,
): boolean {
  return privateClientCompletionMode || selectedOption === 'private client';
}

/** District/state picker only for the United States (same as other register flows). */
export function privateClientRegisterShowsRegionField(countryId: unknown): boolean {
  return assetCreateShowsRegionField(countryId);
}

const PRIVATE_CLIENT_REGISTER_MANDATORY_KEYS_BASE = [
  'email',
  'password',
  'confirm_password',
  'phone_number',
  'country_id',
  'first_name',
  'last_name',
  'birth_date',
] as const;

export function privateClientRegisterMandatoryKeys(countryId?: unknown): readonly string[] {
  if (privateClientRegisterShowsRegionField(countryId)) {
    return [...PRIVATE_CLIENT_REGISTER_MANDATORY_KEYS_BASE, 'state_id'];
  }
  return [...PRIVATE_CLIENT_REGISTER_MANDATORY_KEYS_BASE];
}

/** First missing required root field key, or null when the form can submit. */
export function missingPrivateClientRegisterRequiredField(
  payload: Record<string, unknown>,
  options?: { skipPassword?: boolean },
): string | null {
  for (const key of privateClientRegisterMandatoryKeys(payload.country_id)) {
    if (
      options?.skipPassword &&
      (key === 'password' || key === 'confirm_password')
    ) {
      continue;
    }
    if (isEmptyRegisterValue(payload[key])) {
      return key;
    }
  }
  return null;
}

/** Payload keys shown in DynamicRecordForm for private-client registration. */
export function privateClientRegisterVisibleData(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const out = { ...payload };
  for (const key of PRIVATE_CLIENT_HIDDEN_REGISTER_KEYS) {
    delete out[key];
  }
  if (!privateClientRegisterShowsRegionField(payload.country_id)) {
    delete out.state_id;
  }
  return out;
}

/** Booking profile completion — hide passwords; optionally only missing fields. */
export function privateClientCompletionVisibleData(
  payload: Record<string, unknown>,
  onlyShowFields?: readonly string[],
): Record<string, unknown> {
  const out = privateClientRegisterVisibleData(payload);
  for (const key of PRIVATE_CLIENT_COMPLETION_HIDDEN_KEYS) {
    delete out[key];
  }
  if (onlyShowFields && onlyShowFields.length > 0) {
    const allowed = new Set(onlyShowFields);
    allowed.add('country_id');
    if (privateClientRegisterShowsRegionField(payload.country_id)) {
      allowed.add('state_id');
    }
    for (const key of Object.keys(out)) {
      if (!allowed.has(key)) {
        delete out[key];
      }
    }
  }
  return out;
}

/** Fill backend-required fields omitted from the UI with placeholder values. */
export function withPrivateClientRegisterBackendDefaults(
  payload: Record<string, unknown>,
  regions: readonly GeneralInfoPicklistItem[] = [],
): Record<string, unknown> {
  const out = { ...payload };

  out.address = normalizePrivateClientRegisterAddress(out.address);

  for (const key of ['gender', 'national_id'] as const) {
    const current = out[key];
    if (current === undefined || current === null || current === '') {
      out[key] = PRIVATE_CLIENT_REGISTER_BACKEND_DEFAULTS[key];
    }
  }

  if (!privateClientRegisterShowsRegionField(out.country_id)) {
    const currentRegion = coerceNumericId(out.state_id);
    if (currentRegion === null) {
      const defaultRegionId = resolveIsraelDefaultRegionId(regions);
      out.state_id =
        defaultRegionId !== null
          ? defaultRegionId
          : PRIVATE_CLIENT_REGISTER_BACKEND_DEFAULTS.state_id;
    }
  }

  return out;
}

/** POST /register/PrivateClient body: hidden defaults, no occupation. */
export function preparePrivateClientRegisterPostBody(
  payload: Record<string, unknown>,
  regions: readonly GeneralInfoPicklistItem[] = [],
): Record<string, unknown> {
  const prepared = stripPrivateClientRegisterOmittedKeys(
    withPrivateClientRegisterBackendDefaults(payload, regions),
  );
  const countryId = coerceNumericId(prepared.country_id);
  if (countryId !== null) {
    prepared.country_id = countryId;
  }
  const stateId = coerceNumericId(prepared.state_id);
  if (stateId !== null) {
    prepared.state_id = stateId;
  }
  return prepared;
}

export function mergePrivateClientRegisterEmptyPayload(
  visibleEmpty: Record<string, unknown>,
): Record<string, unknown> {
  return {
    ...withPrivateClientRegisterBackendDefaults({
      ...visibleEmpty,
      occupation: visibleEmpty.occupation ?? '',
    }),
    // Keep country unset so the user must choose (not silently Israel-only).
    country_id: visibleEmpty.country_id ?? null,
    state_id: visibleEmpty.state_id ?? null,
  };
}
